import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createModifier, updateModifier } from '../../../shared/api/products.js'
import type { Modifier } from '../../../shared/types/models.js'
import {
  normalizePositiveInt,
  normalizePrice,
  normalizeSortOrder,
  validateNameField,
  validatePositiveIntField,
  validatePriceField,
  validateSortOrderField,
} from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'

interface Props {
  organizationId: string
  productId: string
  groupId: string
  modifier?: Modifier | null
  disabled: boolean
  pricePrecisionMode?: string
  onDone: () => void
  onCancel: () => void
}

export default function ModifierForm({
  organizationId,
  productId,
  groupId,
  modifier,
  disabled,
  pricePrecisionMode = 'INTEGER_ONLY',
  onDone,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const isEdit = modifier !== undefined && modifier !== null

  const [name, setName] = useState(modifier?.name ?? '')
  const [price, setPrice] = useState(modifier ? String(modifier.price) : '')
  const [maxQuantity, setMaxQuantity] = useState(modifier ? String(modifier.maxQuantity) : '1')
  const [sortOrder, setSortOrder] = useState(modifier ? String(modifier.sortOrder) : '0')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  const idSuffix = isEdit && modifier ? modifier.id : 'new'
  const priceStep = pricePrecisionMode === 'HALF_UNIT' ? '0.5' : '1'

  function clearErrors() {
    if (Object.keys(errors).length > 0) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    if (validateNameField(name) === 'required') fieldErrors.name = t('error.fieldRequired')
    const priceError = validatePriceField(price)
    if (priceError === 'required') fieldErrors.price = t('products.priceRequired')
    else if (priceError === 'invalid') fieldErrors.price = t('products.priceInvalid')
    const qtyError = validatePositiveIntField(maxQuantity)
    if (qtyError === 'required') fieldErrors.maxQuantity = t('products.maxQuantityRequired')
    else if (qtyError === 'invalid') fieldErrors.maxQuantity = t('products.maxQuantityInvalid')
    if (validateSortOrderField(sortOrder)) fieldErrors.sortOrder = t('products.sortOrderInvalid')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const priceValue = normalizePrice(price)
    if (priceValue === undefined) return

    const payload = {
      name: name.trim(),
      price: priceValue,
      maxQuantity: normalizePositiveInt(maxQuantity) ?? 1,
      sortOrder: normalizeSortOrder(sortOrder) ?? 0,
    }

    setSaving(true)
    setErrors({})
    try {
      if (isEdit && modifier) {
        await updateModifier(organizationId, productId, groupId, modifier.id, payload)
      } else {
        await createModifier(organizationId, productId, groupId, payload)
      }
      onDone()
    } catch (err: unknown) {
      setErrors(toFieldErrors(err, ['name', 'price', 'maxQuantity', 'sortOrder'], t('products.modifierError')))
      setSaving(false)
    }
  }

  return (
    <div className="modifier-form settings-card">
      <h4>{isEdit ? t('products.modifierEditTitle') : t('products.modifierCreateTitle')}</h4>
      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`modifierName-${idSuffix}`}>{t('products.name')} *</label>
            <input
              id={`modifierName-${idSuffix}`}
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors()
              }}
              maxLength={200}
              required
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`modifierPrice-${idSuffix}`}>{t('products.price')} *</label>
            <input
              id={`modifierPrice-${idSuffix}`}
              type="number"
              min={0}
              step={priceStep}
              value={price}
              onChange={e => {
                setPrice(e.target.value)
                clearErrors()
              }}
              required
              disabled={saving || disabled}
            />
            {errors.price && <span className="field-error">{errors.price}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`modifierMaxQuantity-${idSuffix}`}>{t('products.maxQuantity')}</label>
            <input
              id={`modifierMaxQuantity-${idSuffix}`}
              type="number"
              min={1}
              step={1}
              value={maxQuantity}
              onChange={e => {
                setMaxQuantity(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.maxQuantity && <span className="field-error">{errors.maxQuantity}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`modifierSortOrder-${idSuffix}`}>{t('products.sortOrder')}</label>
            <input
              id={`modifierSortOrder-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || disabled}>
            {saving ? t('products.saving') : isEdit ? t('products.save') : t('products.addModifier')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('products.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}